import type { NextPageContext } from 'next';
import { createContext, useContext } from 'react';
import { BrowserService } from './BrowserService';
import { CacheService } from './CacheService';
import { FlagrService } from './FlagrService/FlagrService';
import { Globals } from './Globals';
import { LoggerService } from './LoggerService/LoggerService';
import { NetworkService } from './NetworkService';
import { Public } from './Public.type';
import { SsrData, SsrDataPayload } from './SsrData';
import { Flags, makeFlags } from './flags';
import { makeHostName } from './hostName';
import { I18nService } from './i18n/I18nService';
import { ScreenSizeService } from './screenSize/ScreenSizeService';
import { makeServerTimestamp } from './serverTimestamp';
import { ThemeService } from './theme/ThemeService';
import { makeTracingHeaders } from './tracingHeaders';

export interface ServicesInput {
  ssrData?: SsrDataPayload;
  globals?: Globals;
  overrides?: ServiceOverrides;
  pageContext?: NextPageContext;
}

export class Services {
  constructor(private input: ServicesInput) {}

  private overrides = this.input.overrides;
  private req = this.input.pageContext?.req;

  private ssrData = new SsrData(this.input.ssrData);

  public toData = () => this.ssrData.serialise();

  public serverTimestamp = makeServerTimestamp(this.ssrData);

  public network = this.overrides?.network ?? new NetworkService(this.input.globals);

  public browser =
    this.overrides?.browser ?? new BrowserService(this.ssrData, this.input.globals, this.req);

  public screenSize =
    this.overrides?.screenSize ?? new ScreenSizeService(this.ssrData, this.browser);

  public theme = this.overrides?.theme ?? new ThemeService(this.ssrData, this.network);

  public i18n =
    this.overrides?.i18n ??
    new I18nService(this.ssrData, this.browser, this.input.pageContext?.query);

  public flags = this.overrides?.flags ?? makeFlags(this.ssrData, this.input.pageContext?.query);

  public tracingHeaders = makeTracingHeaders(this.req);

  public logger = new LoggerService(this.req, this.flags, this.tracingHeaders);

  public hostName = makeHostName(this.ssrData, this.browser, this.req);

  public cache = this.overrides?.cache ?? new CacheService(this.ssrData);

  public flagr = this.overrides?.flagr ?? new FlagrService(this.network, this.ssrData);
}

declare module 'next' {
  interface NextPageContext {
    services?: Services;
  }
}

const ServicesContext = createContext<null | Services>(null);

export const ServicesProvider = ServicesContext.Provider;

export const useServices = (): Services => {
  const services = useContext(ServicesContext);
  if (services === null) throw new Error('No Services found in context');
  return services;
};

export interface ServiceOverrides {
  browser?: Public<BrowserService>;
  cache?: Public<CacheService>;
  flagr?: Public<FlagrService>;
  flags?: Flags;
  i18n?: Public<I18nService>;
  network?: Public<NetworkService>;
  screenSize?: Public<ScreenSizeService>;
  theme?: Public<ThemeService>;
}
